import classNames from 'classnames';
import type { Delta, EmitterSource, Range } from 'quill/core';
import React, { useEffect, useMemo, useRef } from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useApolloClient } from '@apollo/client';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { IS_APPLE } from '../constants/os';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { usePendingMagneticWallItem } from '../post/PendingMagneticWallItemProvider';
import useShouldShowFullPageSheet from '../post/comment/useShouldShowFullPageSheet';
import { getScrollContainer } from '../post/listenToPostScroll';
import usePerformLoadSafeScrolling from '../post/utils/usePerformLoadSafeScrolling';
import { UIActions } from '../ui/uiSlice';
import Logger from '../utils/Logger';
import { getThreadIdFromUrlHash } from '../utils/url/commentThreadHash';
import useIsPublicView from '../utils/useIsPublicView';
import useShortcut from '../utils/useShortcut';
import { useAssetTokenContext } from './AssetTokenContext';
import type { AssetUploaderOptions } from './AssetUploader';
import AssetUploader from './AssetUploader';
import AttributeHandler from './AttributeHandler';
import type { ClipboardHooks } from './Clipboard';
import { ClipboardHooksContext } from './ClipboardHooksContext';
import * as styles from './Editor.module.scss';
import EditorPlaceholder from './EditorPlaceholder';
import Gutter from './Gutter';
import GutterConfigProvider from './GutterConfigProvider';
import History from './History';
import LinkCursorSelection from './LinkCursorSelection';
import MultiCursor from './MultiCursor';
import QuillComponent from './QuillComponent';
import { useQuill, useQuillReadOnly, useQuillSelection } from './QuillContext';
import Quill from './QuillEditor';
import { getEditorControlsContainer } from './ReactQuill';
import DownloadAttachmentsProvider from './attachment/DownloadAttachmentsProvider';
import EmojiAutocompleter from './controls/EmojiAutocompleter';
import MentionPicker from './controls/MentionPicker';
import SlashCommandMenu from './controls/SlashCommandMenu';
import AttachmentToolbar from './controls/toolbar/AttachmentToolbar';
import ButtonToolbar from './controls/toolbar/ButtonToolbar';
import CardToolbar from './controls/toolbar/CardToolbar';
import EditorToolbar from './controls/toolbar/EditorToolbar';
import EmbedToolbar from './controls/toolbar/EmbedToolbar';
import FileToolbar from './controls/toolbar/FileToolbar';
import FloatingToolbar from './controls/toolbar/FloatingToolbar';
import HintToolbar from './controls/toolbar/HintToolbar';
import ImageToolbar from './controls/toolbar/ImageToolbar';
import VideoToolbar from './controls/toolbar/VideoToolbar';
import { EditorActions } from './editorSlice';
import ImageBlot from './formats/ImageBlot';
import { UserMentionProvider } from './mentions/UserMention';
import EditorModal from './modals/EditorModal';
import useSpellCorrector from './spellCorrector/useSpellCorrector';
import useAICompose from './useAICompose';
import useHeaderCollapse from './useHeaderCollapse';
import useLinkShortcut from './useLinkShortcut';
import useListCollapse from './useListCollapse';
import useSyncMagneticWallStates from './useSyncMagneticWallState';
import useUploadPostAsset from './useUploadPostAsset';
import DraggingAutoScroll from './utils/DraggingAutoScroll';
import copyAsMarkdown from './utils/copyAsMarkdown';
import { doesBlotManageRemoteSelections } from './utils/getNestedTargetForRemoteSelection';
import onDnDDragEnd from './utils/onDnDDragEnd';
import removeMagneticWallAttributors from './utils/removeMagneticWallAttributors';

const EDITOR_MODULE_CONFIG = {
  prefixListener: true,
  headerId: true,
  hoveredBlotListener: true,
};

const POST_FRAGMENT = gql(`
  fragment Editor_post on PostFields {
    id
    ...DownloadAttachmentsProvider_post
    ...EditorPlaceholder_post
    ...EditorToolbar_post
    ...UserMentionProvider_post
  }
`);

const useEditorShortcuts = (isMounted: boolean, isExternalPost: boolean) => {
  const { quill } = useQuill();
  const { range, formats } = useQuillSelection();
  const dispatch = useAppDispatch();
  const isPublicView = useIsPublicView();
  const skip = !isMounted;
  const { createPendingCommentThread } = usePendingMagneticWallItem();

  const handleCommentShortcut = (e: KeyboardEvent) => {
    if (range?.length && !formats.comment && !isExternalPost) {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      createPendingCommentThread();
    }
    e.preventDefault();
  };

  useShortcut(
    'mod+a',
    (e) => {
      quill?.setSelection(0, quill.getLength(), Quill.sources.USER);
      e.preventDefault();
    },
    { skip },
  );

  useShortcut(
    'mod+s',
    (e) => {
      e.preventDefault();
    },
    { global: true, skip },
  );

  useShortcut('mod+option+a', handleCommentShortcut, { global: true, skip });

  // On Mac avoid Chrome's minimize window shortcut
  useShortcut('mod+option+m', handleCommentShortcut, {
    global: true,
    skip: skip || !IS_APPLE,
  });

  // On Windows avoid NVidia GeForce Experience shortcut
  useShortcut('mod+option+m', handleCommentShortcut, {
    global: true,
    action: 'keyup',
    skip: skip || IS_APPLE,
  });

  useShortcut(
    ['e', 'i'],
    (e) => {
      dispatch(EditorActions.readOnly(false));
      e.preventDefault();
    },
    { skip: isExternalPost || isPublicView },
  );

  useShortcut(
    'mod+enter',
    (e) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'TEXTAREA' && target.tagName !== 'INPUT') {
        dispatch(EditorActions.readOnly(true));
        e.preventDefault();
      }
    },
    { global: true, skip: isExternalPost },
  );

  useShortcut(
    'mod+shift+c',
    async (e) => {
      const r = quill?.getSelection();
      if (r?.length && quill) {
        e.preventDefault();
        await copyAsMarkdown(quill, r);
      }
    },
    { global: true },
  );
};

const useShouldAutoFocus = () => {
  const shouldAutoFocusRef = useRef<boolean | null>(null);

  if (typeof shouldAutoFocusRef.current === 'boolean') {
    return shouldAutoFocusRef.current;
  }

  const shouldAutoFocus = !getThreadIdFromUrlHash(document.location.hash);
  shouldAutoFocusRef.current = shouldAutoFocus;
  return shouldAutoFocus;
};

const useBlockPlaceholder = (enabled: boolean) => {
  const { quill } = useQuill();

  useEffect(() => {
    if (!quill || !enabled) return undefined;

    const manager = {
      hasPlaceholder: false, // local flag to improve performance
      getAll() {
        return this.hasPlaceholder
          ? Array.from(
              quill.root.querySelectorAll(`[${styles.placeholderAttribute}]`),
            )
          : [];
      },
      removeAll() {
        this.getAll().forEach((node) =>
          node.removeAttribute(styles.placeholderAttribute),
        );
        this.hasPlaceholder = false;
      },
      set(node: Element) {
        this.removeAll();
        node.setAttribute(styles.placeholderAttribute, '');
        this.hasPlaceholder = true;
      },
    };

    const listener = (
      type: string,
      _old: unknown,
      _newDelta: unknown,
      source: EmitterSource,
    ) => {
      if (type === Quill.events.SELECTION_CHANGE) {
        const selectedNode = quill.selection.lastNative?.start.node;
        if (
          selectedNode instanceof Element &&
          selectedNode.tagName === 'BR' &&
          selectedNode.parentNode instanceof Element &&
          selectedNode.parentNode.tagName === 'P'
        ) {
          manager.set(selectedNode.parentNode);
        } else {
          manager.removeAll();
        }
      } else if (type === Quill.events.TEXT_CHANGE) {
        if (source === Quill.sources.USER) {
          manager.removeAll();
        } else {
          const nodes = manager.getAll();
          const node = nodes[0];
          if (node) {
            const blot = quill.scroll.find(node);
            const isNodeEmpty = blot ? blot.length() <= 1 : false;
            if (isNodeEmpty) {
              nodes
                .slice(1)
                .forEach((n) => n.removeAttribute(styles.placeholderAttribute));
            } else {
              manager.removeAll();
            }
          }
        }
      }
    };

    quill.on(Quill.events.EDITOR_CHANGE, listener);
    return () => {
      quill.off(Quill.events.EDITOR_CHANGE, listener);
      manager.removeAll();
    };
  }, [quill, enabled]);
};

const useDnDAutoScroll = () => {
  const readOnly = useQuillReadOnly();

  useEffect(() => {
    if (readOnly) return undefined;

    const scrollingContainer = getScrollContainer();
    if (!scrollingContainer) return undefined;

    const draggingAutoScroll = new DraggingAutoScroll(scrollingContainer);
    const off = onDnDDragEnd(document.body, () => {
      draggingAutoScroll.stop();
    });

    const dragEnterListener = () => {
      draggingAutoScroll.start();
    };
    const dragOverListener = (e: MouseEvent) => {
      draggingAutoScroll.setMouseYOffset(e.clientY);
    };
    scrollingContainer.addEventListener('dragenter', dragEnterListener);
    scrollingContainer.addEventListener('dragover', dragOverListener);

    return () => {
      off();
      draggingAutoScroll.stop();
      scrollingContainer.removeEventListener('dragenter', dragEnterListener);
      scrollingContainer.removeEventListener('dragover', dragOverListener);
    };
  }, [readOnly]);
};

export type TextChangeHandler = (
  delta: Delta,
  oldDelta: Delta,
  source: string,
) => void;

export type PerformanceMeasureHandler = (durations: {
  applied: number;
  mounted: number;
}) => void;

type EditorNamespace = 'editor' | 'publicEditor';

interface EditorContentProps {
  quill: Quill | null;
  hasSelection: boolean;
  readOnly: boolean;
  post: DocumentType<typeof POST_FRAGMENT>;
  namespace?: EditorNamespace;
  showPlaceholder: boolean;
  onTextChange: TextChangeHandler;
  onPerformanceMeasure?: PerformanceMeasureHandler;
}

function useEditorClipboardHooks() {
  return useMemo((): ClipboardHooks => {
    let lastCutHtml: string | null = null;

    return {
      beforeCut: (html) => {
        lastCutHtml = html;
        return html;
      },
      beforePaste: (html) => {
        const hasBeenCutLocally =
          html.replace("<meta charset='utf-8'>", '') === lastCutHtml;

        // pasting more than once should be treated like a copy,
        // therefore we reset the lastCutHTML state
        lastCutHtml = null;
        if (hasBeenCutLocally) return html;
        return removeMagneticWallAttributors(html);
      },
    };
  }, []);
}

const EditorContent = React.memo(
  ({
    hasSelection,
    quill,
    readOnly,
    post,
    namespace = 'editor',
    showPlaceholder,
    onTextChange,
    onPerformanceMeasure,
  }: EditorContentProps) => {
    const initialContent = useAppSelector(
      (state) => state.editor.initialContent,
    );
    const title = useAppSelector((state) => state.editor.title);
    const shouldAutoFocus = useShouldAutoFocus();
    const isPublicView = useIsPublicView();

    const isQuillMounted = useAppSelector((state) => !!state.editor.quill);
    useEditorShortcuts(
      isQuillMounted,
      'externalPost' in post && !!post.externalPost,
    );
    const dispatch = useAppDispatch();
    const client = useApolloClient();

    useBlockPlaceholder(!showPlaceholder);

    const handleSelectionChange = (
      newRange: Range | null,
      yChanged?: boolean,
    ) => {
      if (newRange == null) {
        if (hasSelection) {
          dispatch(EditorActions.transmitStateChange(null));
        }
        return;
      }

      if (!yChanged) return;

      if (newRange.length <= 1) {
        const [leaf] = quill?.getLeaf(newRange.index) || [];
        if (leaf && doesBlotManageRemoteSelections(leaf)) {
          return;
        }
      }

      dispatch(
        EditorActions.transmitStateChange({ type: 'plain', ...newRange }),
      );
    };

    const handleTextChange = (
      delta: Delta,
      oldContent: Delta,
      source: string,
    ) => {
      if (source === Quill.sources.USER) {
        dispatch(EditorActions.localUpdated(delta));
        const newTitle = quill?.getTitle();
        if (
          newTitle !== undefined &&
          newTitle !== title &&
          !(newTitle === '' && title === 'Untitled')
        ) {
          dispatch(EditorActions.titleUpdate(newTitle, isPublicView));

          // eslint-disable-next-line slab-graphql/no-unused-fields
          const fragment = gql(`
            fragment updatePostTitle on Post {
              id
              title
            }
          `);

          client.writeFragment({
            id: client.cache.identify(post),
            fragment,
            fragmentName: 'updatePostTitle',
            data: { id: post.id, title: newTitle },
          });
        }
      } else if (source !== Quill.sources.API) {
        Logger.message(`Unknown source in onTextChange ${source}`);
      }
      if (onTextChange) onTextChange(delta, oldContent, source);
    };

    const clipboardHooks = useEditorClipboardHooks();

    const uploadPostAsset = useUploadPostAsset(post.id);
    const assetUploaderOptions: AssetUploaderOptions = {
      sendFileToServer: uploadPostAsset,
      canReuseFileUrl: (source) =>
        typeof source === 'string' &&
        source.includes(window.__slab.STATIC_ORIGIN) &&
        source.includes(`/${post.id}/`),
    };

    return (
      <DownloadAttachmentsProvider post={post}>
        <ClipboardHooksContext.Provider value={clipboardHooks}>
          {quill && (
            <>
              <EditorToolbar
                showInTitleLine
                readOnly={readOnly}
                post={post}
                portalContainer="disabled"
              />
              <MentionPicker quill={quill} readOnly={readOnly} />
              <EmojiAutocompleter quill={quill} readOnly={readOnly} />
              <ImageToolbar />
              <HintToolbar />
              <EmbedToolbar />
              <CardToolbar />
              <VideoToolbar />
              <AttachmentToolbar />
              <ButtonToolbar />
              <FileToolbar />
            </>
          )}
          <GutterConfigProvider
            config={{
              listCollapsingEnabled: !readOnly,
              reorderingEnabled: !readOnly,
            }}
          >
            <UserMentionProvider post={post}>
              <QuillComponent
                preloadNearScrollingContainerBlots
                autoFocus={shouldAutoFocus}
                namespace={namespace}
                rootClassName={classNames(styles.editor, 'post-editor')}
                content={initialContent || undefined}
                autoHeight={showPlaceholder}
                contentPlaceholder={
                  showPlaceholder || readOnly
                    ? ''
                    : 'Put your ideas to paper...'
                }
                readOnly={readOnly}
                moduleConfig={{
                  ...EDITOR_MODULE_CONFIG,
                  clipboard: { clipboardHooks },
                  [AssetUploader.moduleName]: assetUploaderOptions,
                }}
                onSelectionChange={handleSelectionChange}
                onTextChange={handleTextChange}
                onMount={(mounted) => {
                  dispatch(EditorActions.editorMounted(mounted));
                }}
                onPerformanceMeasure={onPerformanceMeasure}
              />
            </UserMentionProvider>
            {quill && (
              <>
                <MultiCursor quill={quill} />
                {showPlaceholder && <EditorPlaceholder post={post} />}
                <LinkCursorSelection />
                <SlashCommandMenu quill={quill} readOnly={readOnly} />
                <Gutter
                  quill={quill}
                  hoverableAreaContainer={
                    getEditorControlsContainer(quill) ?? null
                  }
                />
              </>
            )}
          </GutterConfigProvider>
        </ClipboardHooksContext.Provider>
      </DownloadAttachmentsProvider>
    );
  },
);

EditorContent.displayName = 'EditorContent';

interface EditorProps {
  post: DocumentType<typeof POST_FRAGMENT>;
  namespace?: EditorNamespace;
  highlightedRangeIndex?: number;
  showPlaceholder: boolean;
  onTextChange: TextChangeHandler;
  onPerformanceMeasure?: PerformanceMeasureHandler;
}

const Editor = ({
  post,
  namespace,
  highlightedRangeIndex,
  showPlaceholder,
  onTextChange,
  onPerformanceMeasure,
}: EditorProps) => {
  const { quill } = useQuill();
  const selection = useQuillSelection();
  useAICompose(post.id);
  useSpellCorrector(post.id);
  const dispatch = useAppDispatch();
  useLinkShortcut();

  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const magneticWallHighlight = useAppSelector(
    (state) => state.editor.magneticWallHighlight,
  );

  useSyncMagneticWallStates({ magneticWallFocus, magneticWallHighlight });
  useDnDAutoScroll();
  useHeaderCollapse(quill);
  useListCollapse(quill);
  const { entityAssetToken } = useAssetTokenContext();

  const version = useAppSelector((state) => state.editor.version);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const performLoadSafeScrolling = useEffectEvent(
    usePerformLoadSafeScrolling(),
  );
  useEffect(() => {
    if (!quill || !highlightedRangeIndex) return undefined;

    return performLoadSafeScrolling(quill, () => {
      const [leaf] = quill.getLeaf(highlightedRangeIndex);
      const node = leaf?.domNode;
      if (node instanceof Element) {
        node.scrollIntoView();
      } else if (node instanceof Node) {
        node.parentElement?.scrollIntoView();
      }
    });
  }, [highlightedRangeIndex, quill]);

  useEffect(() => {
    if (quill?.history instanceof History) {
      quill.history.postIdForTrackingUndo = post.id;
    }
  }, [quill, post.id]);

  const handleDoubleClick = (e: React.MouseEvent) => {
    const { target } = e;

    if (entityAssetToken && ImageBlot.isImageNode(target)) {
      const blot = Quill.find(target, true);
      if (blot instanceof ImageBlot) {
        const { images, selectedIndex } = blot.getData();
        dispatch(
          UIActions.openModal(EditorModal, {
            carousel: {
              images,
              selectedIndex,
              entityAssetToken,
            },
            underlayType: 'carouselSlider',
          }),
        );
      }
    }
  };

  const shouldShowFullPageSheet = useShouldShowFullPageSheet();

  return (
    <FloatingToolbar.Root>
      <AttributeHandler
        onAttributeClick={(attribute, type) => {
          const shouldShowPopOver = readOnly || !shouldShowFullPageSheet;
          dispatch(
            EditorActions.focusMagneticWall({
              id: attribute.id,
              type,
              isActive: shouldShowPopOver,
              hasExpandIntent: false,
            }),
          );
        }}
        asChild
      >
        <div
          className={styles.contentContainer}
          onDoubleClick={handleDoubleClick}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          data-version={version}
        >
          <EditorContent
            post={post}
            namespace={namespace}
            quill={quill}
            readOnly={readOnly}
            hasSelection={!!selection.range}
            showPlaceholder={showPlaceholder}
            onTextChange={onTextChange}
            onPerformanceMeasure={onPerformanceMeasure}
          />
        </div>
      </AttributeHandler>
    </FloatingToolbar.Root>
  );
};

export default Editor;
